import type { Scene } from 'three';
import {
  AmbientLight,
  BoxGeometry,
  CircleGeometry,
  Color,
  DoubleSide,
  Fog,
  Group,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  Path,
  PointLight,
  Shape,
  ShapeGeometry,
  TorusGeometry,
  Vector3,
} from 'three';
import type { LevelDefinition } from '../world/LevelDefinition';
import type { ModelLibrary } from './ModelLibrary';
import type { Room } from '../world/Room';
import type { Surface, SurfaceSegment } from '../world/Surface';
import { quaternionFromBasis } from '../utils/math';

const DEFAULT_WALL = 0x2a3038;
const DEFAULT_FLOOR = 0x1b2026;
const DEFAULT_CEILING = 0xc9d6e2;
const DEFAULT_PATH = 0xffb347;

/** Build the visible level: zone shells with door holes, cover, path and lights. */
export function buildLevelMeshes(
  scene: Scene,
  definition: LevelDefinition,
  room: Room,
  models: ModelLibrary | null = null,
): Group {
  const group = new Group();
  const style = definition.style ?? {};
  const wallMaterial = new MeshStandardMaterial({
    color: style.wallColor ?? DEFAULT_WALL,
    roughness: 0.85,
    metalness: 0.25,
    side: DoubleSide,
  });
  const floorMaterial = new MeshStandardMaterial({
    color: style.floorColor ?? DEFAULT_FLOOR,
    roughness: 0.9,
    metalness: 0.2,
    side: DoubleSide,
  });
  const ceilingMaterial = new MeshStandardMaterial({
    color: style.ceilingColor ?? DEFAULT_CEILING,
    roughness: 0.7,
    metalness: 0.1,
    side: DoubleSide,
    emissive: 0x1b2229,
    emissiveIntensity: 0.4,
  });
  // Exterior hull reads colder, heavier and striped, so the player can tell a
  // breachable wall from an interior partition at a glance.
  const hullMaterial = new MeshStandardMaterial({
    color: 0x27384a,
    roughness: 0.55,
    metalness: 0.65,
    side: DoubleSide,
    emissive: 0x0b1724,
    emissiveIntensity: 0.5,
  });

  // Every face is drawn from its own segments. A shared wall is split between
  // the two zones at build time, so each piece of metal is drawn exactly once.
  for (const surface of room.surfaces.values()) {
    for (const segment of surface.segments) {
      const material = segment.hull
        ? hullMaterial
        : surface.id === 'floor'
          ? floorMaterial
          : surface.id === 'ceiling'
            ? ceilingMaterial
            : wallMaterial;
      group.add(buildSurfaceMesh(surface, segment, material));
      if (segment.hull) group.add(buildHullWindows(surface, segment));
    }
  }

  buildFloorPath(group, definition, style.pathColor ?? DEFAULT_PATH);
  buildCeilingLights(group, definition);
  buildCover(group, definition, models);

  scene.add(group);
  return group;
}

function buildSurfaceMesh(
  surface: Surface,
  segment: SurfaceSegment,
  material: MeshStandardMaterial,
): Mesh {
  const shape = new Shape();
  shape.moveTo(segment.uMin, segment.vMin);
  shape.lineTo(segment.uMax, segment.vMin);
  shape.lineTo(segment.uMax, segment.vMax);
  shape.lineTo(segment.uMin, segment.vMax);
  shape.closePath();
  for (const hole of surface.holes) {
    // A hole is authored in the full face's coordinates; only the piece that
    // the opening actually cuts needs it.
    const overlapU = Math.min(hole.uMax, segment.uMax) - Math.max(hole.uMin, segment.uMin);
    const overlapV = Math.min(hole.vMax, segment.vMax) - Math.max(hole.vMin, segment.vMin);
    if (overlapU <= 0 || overlapV <= 0) continue;
    const path = new Path();
    path.moveTo(hole.uMin, hole.vMin);
    path.lineTo(hole.uMax, hole.vMin);
    path.lineTo(hole.uMax, hole.vMax);
    path.lineTo(hole.uMin, hole.vMax);
    path.closePath();
    shape.holes.push(path);
  }

  const mesh = new Mesh(new ShapeGeometry(shape), material);
  mesh.position.copy(surface.origin.clone());
  mesh.quaternion.copy(quaternionFromBasis(surface.right, surface.forward, surface.normal));
  return mesh;
}

/**
 * A row of viewports on an exterior wall, with a dark space disc and a few
 * stars. Procedural, so no textures are needed.
 */
function buildHullWindows(surface: Surface, segment: SurfaceSegment): Group {
  const group = new Group();
  group.userData.hullWindows = true;
  const frameMaterial = new MeshStandardMaterial({
    color: 0x141c26,
    roughness: 0.4,
    metalness: 0.8,
  });
  const spaceMaterial = new MeshBasicMaterial({ color: 0x03060d });
  const starMaterial = new MeshBasicMaterial({ color: 0xcfe8ff });
  const orientation = quaternionFromBasis(surface.right, surface.forward, surface.normal);

  const count = 3;
  for (let index = 1; index <= count; index += 1) {
    const u = segment.uMin + ((segment.uMax - segment.uMin) * index) / (count + 1);
    const v = segment.vMin + (segment.vMax - segment.vMin) * (index % 2 === 0 ? 0.62 : 0.45);
    const centre = surface.toWorld(u, v, 0.04, new Vector3());

    const ring = new Mesh(new TorusGeometry(0.3, 0.05, 8, 20), frameMaterial);
    ring.position.copy(centre);
    ring.quaternion.copy(orientation);
    group.add(ring);

    const glass = new Mesh(new CircleGeometry(0.28, 20), spaceMaterial);
    glass.position.copy(surface.toWorld(u, v, 0.045, new Vector3()));
    glass.quaternion.copy(orientation);
    group.add(glass);

    for (let star = 0; star < 3; star += 1) {
      const dot = new Mesh(new CircleGeometry(0.02, 6), starMaterial);
      dot.position.copy(surface.toWorld(u + (star - 1) * 0.12, v + (star % 2) * 0.1, 0.05));
      dot.quaternion.copy(orientation);
      group.add(dot);
    }
  }
  return group;
}

function buildFloorPath(group: Group, definition: LevelDefinition, color: number): void {
  const path = definition.style?.path ?? [];
  for (const segment of path) {
    const [fromX, fromZ] = segment.from;
    const [toX, toZ] = segment.to;
    const dx = toX - fromX;
    const dz = toZ - fromZ;
    const length = Math.hypot(dx, dz);
    if (length < 1e-4) continue;
    const width = segment.width ?? 2.4;
    const strip = new Mesh(new BoxGeometry(length, 0.04, width), new MeshBasicMaterial({ color }));
    strip.position.set((fromX + toX) / 2, 0.03, (fromZ + toZ) / 2);
    strip.rotation.y = Math.atan2(-dz, dx);
    group.add(strip);
  }
}

function buildCeilingLights(group: Group, definition: LevelDefinition): void {
  const lights = definition.style?.ceilingLights ?? [];
  const material = new MeshBasicMaterial({ color: 0xffffff });
  for (const light of lights) {
    const [w, h, d] = light.size;
    const panel = new Mesh(new BoxGeometry(w, h, d), material);
    panel.position.set(...light.position);
    group.add(panel);
  }
}

function buildCover(group: Group, definition: LevelDefinition, models: ModelLibrary | null): void {
  // A level with no cover adds no empty group: the mesh count is read by the
  // audit tests, and an empty group is one more thing to walk for nothing.
  if (definition.obstacles.length === 0) return;
  const cover = buildObstacles(definition.obstacles, models);
  // Tagged so the cover can be redrawn once the models finish loading.
  cover.userData.cover = true;
  group.add(cover);
}

/**
 * Redraw a level's cover, usually once its prop models have loaded.
 *
 * The procedural boxes are what a level boots with, so this is the upgrade path
 * rather than the only path: the cover is found by its tag and replaced whole.
 */
export function rebuildCover(
  group: Group,
  definition: LevelDefinition,
  models: ModelLibrary | null,
): void {
  const existing = group.children.find((child) => child.userData.cover === true) as
    Group | undefined;
  if (existing) {
    group.remove(existing);
    disposeLevelMeshes(existing);
  }
  if (definition.obstacles.length === 0) return;
  const cover = buildObstacles(definition.obstacles, models);
  cover.userData.cover = true;
  group.add(cover);
}

/**
 * The box shapes of a level's cover, as one group.
 *
 * Split out because the map builder draws exactly the same thing for the pieces
 * it places: the same crate has to look the same whether it came from the
 * catalog or from the scanner.
 */
export function buildObstacles(
  obstacles: ReadonlyArray<{
    kind?: string;
    position: [number, number, number];
    size: [number, number, number];
  }>,
  models: ModelLibrary | null = null,
): Group {
  const group = new Group();
  const pillarMaterial = new MeshStandardMaterial({
    color: 0x3d4653,
    roughness: 0.7,
    metalness: 0.4,
  });
  const accentMaterial = new MeshStandardMaterial({
    color: 0x63f0d0,
    roughness: 0.3,
    metalness: 0.1,
    emissive: 0x1c6f60,
    emissiveIntensity: 0.6,
  });
  const consoleMaterial = new MeshStandardMaterial({
    color: 0x2f3542,
    roughness: 0.6,
    metalness: 0.5,
  });
  const barrierMaterial = new MeshStandardMaterial({
    color: 0x4a4033,
    roughness: 0.85,
    metalness: 0.15,
  });

  for (const obstacle of obstacles) {
    const [x, y, z] = obstacle.position;
    const [sx, sy, sz] = obstacle.size;
    const kind = obstacle.kind ?? 'crate';
    const model = models?.instance(kind, [sx, sy, sz]);
    if (model) {
      model.position.set(x, y, z);
      group.add(model);
      continue;
    }
    if (kind === 'pillar') {
      const pillar = new Group();
      const base = new Mesh(new BoxGeometry(sx, 0.18, sz), accentMaterial);
      base.position.y = -sy / 2 + 0.09;
      const shaft = new Mesh(new BoxGeometry(sx * 0.78, sy - 0.36, sz * 0.78), pillarMaterial);
      const capital = new Mesh(new BoxGeometry(sx, 0.18, sz), accentMaterial);
      capital.position.y = sy / 2 - 0.09;
      pillar.add(base, shaft, capital);
      pillar.position.set(x, y, z);
      group.add(pillar);
    } else if (kind === 'console') {
      const console = new Group();
      const body = new Mesh(new BoxGeometry(sx, sy, sz), consoleMaterial);
      const stripe = new Mesh(new BoxGeometry(sx * 0.9, sy * 0.16, 0.05), accentMaterial);
      stripe.position.set(0, sy * 0.25, -sz / 2 - 0.02);
      console.add(body, stripe);
      console.position.set(x, y, z);
      group.add(console);
    } else if (kind === 'barrier') {
      // A low wall: a slab with a lit cap, so it reads as cover rather than
      // cargo.
      const barrier = new Group();
      const slab = new Mesh(new BoxGeometry(sx, sy, sz), barrierMaterial);
      const cap = new Mesh(new BoxGeometry(sx, 0.08, sz * 1.05), accentMaterial);
      cap.position.y = sy / 2 - 0.04;
      barrier.add(slab, cap);
      barrier.position.set(x, y, z);
      group.add(barrier);
    } else {
      const crate = new Mesh(new BoxGeometry(sx, sy, sz), consoleMaterial);
      crate.position.set(x, y, z);
      group.add(crate);
    }
  }
  return group;
}

/** Lighting rig and atmosphere for a level. */
export function buildLighting(scene: Scene, definition: LevelDefinition): Group {
  const group = new Group();
  const [width, height, depth] = definition.size;
  const reach = Math.max(width, height, depth);

  // Three.js uses physical light units, so a level this size needs a lot of
  // candela to read properly.
  const ambient = new AmbientLight(0x6c7a8c, 1.6);
  group.add(ambient);

  const panels = definition.style?.ceilingLights ?? [];
  // Keep the point-light budget small: at most six follow the ceiling panels.
  const step = Math.max(1, Math.ceil(panels.length / 6));
  panels.forEach((panel, index) => {
    if (index % step !== 0) return;
    const light = new PointLight(0xeaf4ff, 900, reach * 0.9, 2);
    light.position.set(panel.position[0], Math.max(1, panel.position[1] - 0.2), panel.position[2]);
    group.add(light);
  });

  if (panels.length === 0) {
    const key = new PointLight(0xbfe4ff, 1500, reach * 1.6, 2);
    key.position.set(0, height * 0.8, 0);
    group.add(key);
  }

  scene.add(group);
  scene.fog = new Fog(new Color(0x0a0d12), reach * 0.9, reach * 2.4);
  scene.background = new Color(0x05070a);
  return group;
}

/** Dispose the geometries and materials a level build created. */
export function disposeLevelMeshes(group: Group): void {
  group.traverse((object) => {
    const mesh = object as Mesh & { geometry?: { dispose(): void }; material?: unknown };
    mesh.geometry?.dispose?.();
    const material = mesh.material;
    if (Array.isArray(material)) {
      for (const entry of material) (entry as { dispose?(): void }).dispose?.();
    } else {
      (material as { dispose?(): void } | undefined)?.dispose?.();
    }
  });
}
